import { ReactComponent as Redirect } from "assets/icons/redirect.svg";
import { format } from "date-fns";
import { classNames } from "helpers/ClassNameHelper";
import { Button, Field, useAccessToken, WEEKDAYS_LONG } from "modules/common";
import React, { useState } from "react";
import {
    downloadEducationCertificate,
    Education,
    EducationStatusId,
    STATUS_TITLES,
} from "services";
import { EducationReminderModal } from "./EducationReminderModal";

const EDUCATION_LINK = "https://akis.mps.hr/edukacije";

interface Props {
    education: Education;
    status: EducationStatusId;
}

export const EducationDetails: React.FC<Props> = ({ education, status }) => {
    const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
    // const [isSignupModalOpen, setIsSignupModalOpen] = useState(false); // this might be a mobile only feature, if not query funtions should be implemented as well as design
    const accessToken = useAccessToken();

    const onDownloadCertificate = async () => {
        if (!education?.educationStatuses) return;
        const educationStatus = education.educationStatuses[0];
        await downloadEducationCertificate(accessToken!, educationStatus.uid);
    };

    return (
        <>
            <div className="education-details">
                {/* Header Section */}
                <div className="f-c g-16">
                    <h2 className="education-details-title">{education.title}</h2>
                    <div className="f f-nw g-8 f-r">
                        <h3 className="education-details-date">
                            {WEEKDAYS_LONG[new Date(education.startDate).getDay()]},{" "}
                            {format(new Date(education.startDate), "dd.MM.yyyy.")}
                        </h3>
                        <h3
                            className={classNames(
                                "education-details-date",
                                Boolean(status) && "education-details-date--active"
                            )}
                        >
                            {`${format(new Date(education.startDate), "HH:mm")} - ${format(
                                new Date(education.endDate),
                                "HH:mm"
                            )}`}
                        </h3>
                    </div>
                    <span className="education-details-location">{education.address}</span>
                    <div className="f-r g-8 chip-container">
                        {education.county && (
                            <span className="education-details-subheading education-details-subheading--first">
                                {education.county}
                            </span>
                        )}
                        {education.expertArea && (
                            <span className="education-details-subheading education-details-subheading--second">
                                {education.expertArea}
                            </span>
                        )}
                        {education.measure && (
                            <span className="education-details-subheading education-details-subheading--third">
                                {education.measure}
                            </span>
                        )}
                    </div>
                </div>

                <div className="separator" />

                {/* Reminder Section */}
                {status === EducationStatusId.Applied &&
                    new Date(education.registrationDeadline) > new Date() && (
                        <div className="f-c g-12">
                            <div>
                                <Button
                                    size="xxsmall"
                                    variant={
                                        education.educationReminders.length !== 0
                                            ? "primary"
                                            : "primary-ghost"
                                    }
                                    onClick={() => setIsReminderModalOpen(prev => !prev)}
                                >
                                    Podsjeti me
                                </Button>
                            </div>

                            {/* Mobile only feature? */}
                            {/* <Button
                                size="xxsmall"
                                variant="primary-ghost"
                                className="btn--nowrap"
                                isDisabled={new Date(education.registrationDeadline) <= new Date()}
                                onClick={() => setIsSignupModalOpen(true)}
                            >
                                Prijavi se
                            </Button> */}

                            <EducationReminderModal
                                isReminderModalOpen={isReminderModalOpen}
                                onClose={() => setIsReminderModalOpen(false)}
                                education={education}
                            />
                        </div>
                    )}

                {status === EducationStatusId.Listened && (
                    <div>
                        <Button
                            size="xxsmall"
                            variant="primary-ghost"
                            className="btn--nowrap"
                            onClick={onDownloadCertificate}
                        >
                            Preuzmite potvrdu <Redirect className="ml-8" />
                        </Button>
                    </div>
                )}

                {/* Details Section */}
                <div className="education-details-information">
                    <Field name="Kapacitet">
                        <span>{`${education.spotsAvailable}/${education.maxAttendees}`}</span>
                    </Field>

                    <Field name="Obaveza">
                        <span
                            className={classNames(
                                "education-details-obligation",
                                education.isEducationMandatoryForFarmer &&
                                    "education-details-obligation--active"
                            )}
                        >
                            {education.isEducationMandatoryForFarmer ? "Da" : "Ne"}
                        </span>
                    </Field>

                    <Field name="Predavači">
                        <div className="f-c g-12">
                            {education.instructors.map(value => (
                                <span key={value.name}>{value.name}</span>
                            ))}
                        </div>
                    </Field>

                    <Field name="Napomena">
                        <div className="f-c">
                            <span>{education.remark}</span>
                            <span>
                                Prijavite se na tečaj preko stranice -{" "}
                                <a href={EDUCATION_LINK} target="_blank" rel="noopener noreferrer">
                                    {EDUCATION_LINK}
                                </a>
                            </span>
                        </div>
                    </Field>

                    {/* Status Section */}
                    {status && (
                        <Field name={STATUS_TITLES[status]}>
                            <span className="f">
                                {education.educationStatuses
                                    .map(value => `${value.farmerName} ${value.farmerLastName}`)
                                    .join(",")}
                            </span>
                        </Field>
                    )}
                </div>
            </div>

            {/* Mobile only feature? */}
            {/* <EducationSignupModal
                education={education}
                toggleModal={() => setIsSignupModalOpen(false)}
                isActive={isSignupModalOpen}
            /> */}
        </>
    );
};
